/*
 * @Author: Ryan
 * @Date: 2026-01-27
 * @Description: UGC Scale Section - Create AI-Powered UGC Videos at Scale
 */
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import React, { useState } from 'react';

import styles from './index.module.scss';

export interface ScaleTabData {
  key: string;
  label: string;
  title: string;
  description: string;
  imageUrl: string;
  ctaText: string;
  ctaUrl: string;
}

const DEFAULT_TABS: ScaleTabData[] = [
  {
    key: 'video-generation',
    label: 'Video Generation',
    title: 'Video Generation',
    description:
      'Use the AI video generator to create UGC-style product videos for different colors, styles, and product features. Launch faster and test more creatives without manual production.',
    imageUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/create-ai-powered-ugc-videos-at-scale-video-generation.webp',
    ctaText: 'Create Your Video Now',
    ctaUrl: '/ai-video-generator',
  },
  {
    key: 'product-try-on',
    label: 'Product Try-On',
    title: 'Product Try-On',
    description:
      'Show how products look in real scenarios with AI product avatars. Enable virtual try-ons for clothing, accessories, and beauty products to help customers visualize before purchasing.',
    imageUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/create-ai-powered-ugc-videos-at-scale-product-try-on.webp',
    ctaText: 'Try Product Avatar',
    ctaUrl: '/ai-product-avatar',
  },
  {
    key: 'model-swap',
    label: 'Model Swap',
    title: 'Model Swap',
    description:
      'Easily swap avatars and edit videos with the AI video editor. Create multiple versions from one video to match different audiences, platforms, or regions — no reshoots needed.',
    imageUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/create-ai-powered-ugc-videos-at-scale-model-swap.webp',
    ctaText: 'Start Editing Now',
    ctaUrl: '/ai-video-editor',
  },
];

interface UGCScaleSectionProps {
  title?: string;
  subtitle?: string;
  tabs?: ScaleTabData[];
}

const UGCScaleSection: React.FC<UGCScaleSectionProps> = ({ title, subtitle, tabs }) => {
  const tabsData = tabs || DEFAULT_TABS;
  const [activeTab, setActiveTab] = useState(0);
  const current = tabsData[activeTab];

  return (
    <>
      {/* 预加载三张 Tab 图片 */}
      <Head>
        {tabsData.map((tab) => (
          <link key={tab.key} rel="preload" as="image" href={tab.imageUrl} />
        ))}
      </Head>
      <section className={styles.section}>
        <div className={styles.container}>
          {/* 标题 */}
          <div className={styles.header}>
            <h2 className={styles.title}>
              {title || 'Create AI-Powered UGC Videos at Scale'}
            </h2>
            <p className={styles.subtitle}>
              {subtitle ||
                'Generate, edit, and customize UGC-style product videos with AI. From video creation to avatar-based showcases, everything is designed to help brands produce high-performing video content faster and at scale.'}
            </p>
          </div>

          {/* Tab 切换 */}
          <div className={styles.tabs}>
            {tabsData.map((tab, index) => (
              <button
                key={tab.key}
                type="button"
                className={`${styles.tab} ${activeTab === index ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(index)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 内容区域 */}
          <div className={styles.contentWrapper}>
            <div className={styles.content}>
              {/* 左侧文字 */}
              <div className={styles.textSide}>
                <h3 className={styles.contentTitle}>{current.title}</h3>
                <p className={styles.contentDescription}>{current.description}</p>
                <Link href={current.ctaUrl} className={styles.ctaButton}>
                  {current.ctaText}
                </Link>
              </div>

              {/* 右侧图片 */}
              <div className={styles.imageSide}>
                <Image
                  src={current.imageUrl}
                  alt={current.title}
                  width={720}
                  height={480}
                  className={styles.image}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default UGCScaleSection;
